import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.tsx'
import { ThemeProvider } from '@/components/theme-provider'
import { StoreProvider } from './store/Store.tsx'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
  RouterProvider,
} from 'react-router-dom'
import {
  About,
  BilansPage,
  BillDetailPage,
  BillsPage,
  CategoriesPage,
  ContainerDetailPage,
  ContainersPage,
  CustomerAccountPage,
  Customers,
  Dash,
  DepotDetailPage,
  DepotsPage,
  Doc,
  Expenses,
  ForgotPassword,
  Homepage,
  InventoryPage,
  ItemDetailPage,
  ItemsPage,
  Login,
  ManualPayment,
  PlatformAdminsPage,
  PlatformAuditPage,
  PlatformCampaignsPage,
  PlatformLayout,
  PlatformLoginPage,
  PlatformOwnerDetailPage,
  PlatformOwnersPage,
  Pricing,
  Profil,
  Register,
  ResetPassword,
  UsersPage,
  VerifyEmail,
  PlatformPacksPage,
  PlatformRegistrationBlocksPage,
} from './pages/index.ts'
import ProtectedRoute from './components/ProtectedRoute.tsx'
import { Toaster } from '@/components/ui/sonner'
import { TooltipProvider } from './components/ui/tooltip.tsx'
import { RequireRole } from './components/RequireRole.tsx'
import { PlatformStoreProvider } from './store/PlatformStore.tsx'
import ProtectedPlatformRoute from './components/ProtectedPlatformRoute.tsx'
import RouteErrorBoundary from './components/RouteErrorBoundary.tsx'

const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      <Route path='/login' element={<Login />} />
      <Route path='/platform/login' element={<PlatformLoginPage />} />
      <Route path='/register' element={<Register />} />
      <Route path='/verify-email' element={<VerifyEmail />} />
      <Route path='/forgot-password' element={<ForgotPassword />} />
      <Route path='/reset-password' element={<ResetPassword />} />
      <Route path='/pricing' element={<Pricing />} />
      <Route path='/documentation' element={<Doc />} />
      <Route path='/about' element={<About />} />
      <Route path='/billing/manual-payment' element={<ManualPayment />} />

      <Route path='/' element={<App />} errorElement={<RouteErrorBoundary />}>
        <Route index={true} path='/' element={<Homepage />} />

        {/**Norma Users */}
        <Route path='' element={<ProtectedRoute />}>
          <Route path='/dashboard' element={<Dash />} />
          <Route path='/customers' element={<Customers />} />
          <Route path='/customers/:id' element={<CustomerAccountPage />} />
          <Route path='/containers' element={<ContainersPage />} />
          <Route path='/containers/:id' element={<ContainerDetailPage />} />
          <Route path='/depots/:id' element={<DepotDetailPage />} />
          <Route path='/depots' element={<DepotsPage />} />
          <Route path='/bills' element={<BillsPage />} />
          <Route path='/bills/:id' element={<BillDetailPage />} />
          <Route path='/categories' element={<CategoriesPage />} />
          <Route path='/items' element={<ItemsPage />} />
          <Route path='/items/:id' element={<ItemDetailPage />} />
          <Route path='/me' element={<Profil />} />
          <Route path='/expenses' element={<Expenses />} />

          <Route element={<RequireRole roles={['OWNER', 'ADMIN']} />}>
            <Route path='/bilans' element={<BilansPage />} />
            <Route path='/inventory' element={<InventoryPage />} />
            <Route path='/users' element={<UsersPage />} />
          </Route>

        </Route>
      </Route>

      <Route
        path='/platform'
        element={<PlatformLayout />}
        errorElement={<RouteErrorBoundary />}
      >
        <Route element={<ProtectedPlatformRoute />}>
          <Route index element={<PlatformOwnersPage />} />
          <Route path='owners/:ownerId' element={<PlatformOwnerDetailPage />} />
          <Route path='audit' element={<PlatformAuditPage />} />
          <Route path='admins' element={<PlatformAdminsPage />} />
          <Route path='packs' element={<PlatformPacksPage />} />
          <Route path='campaigns' element={<PlatformCampaignsPage />} />
          <Route
            path='registration-blocks'
            element={<PlatformRegistrationBlocksPage />}
          />
        </Route>
      </Route>
    </>,
  ),
)

export const queryClient = new QueryClient()

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ThemeProvider defaultTheme='system' storageKey='vite-ui-theme'>
      <TooltipProvider>
        <StoreProvider>
          <PlatformStoreProvider>
            <QueryClientProvider client={queryClient}>
              <RouterProvider router={router} />
              <ReactQueryDevtools initialIsOpen={false} />
            </QueryClientProvider>
            <Toaster position='top-right' richColors closeButton />
          </PlatformStoreProvider>
        </StoreProvider>
      </TooltipProvider>
    </ThemeProvider>
  </StrictMode>,
)
